import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { X, Palette, Zap, Users, MessageCircle } from "lucide-react";

interface ExitIntentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onFeedbackSubmit: (feedback: { reason: string; message: string }) => void;
}

const ExitIntentPopup = ({
  isOpen,
  onClose,
  onFeedbackSubmit,
}: ExitIntentPopupProps) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) return;

    setIsSubmitting(true);
    try {
      await onFeedbackSubmit({
        reason: selectedReason,
        message,
      });
      onClose();
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const reasons = [
    {
      id: "confusing",
      label: "Too confusing",
      description: "The interface was hard to use",
      icon: Palette,
      color: "text-red-500",
    },
    {
      id: "slow",
      label: "Too slow",
      description: "The platform was loading slowly",
      icon: Zap,
      color: "text-orange-500",
    },
    {
      id: "missing",
      label: "Missing features",
      description: "I couldn't find what I needed",
      icon: Users,
      color: "text-blue-500",
    },
    {
      id: "other",
      label: "Something else",
      description: "Tell us what we can improve",
      icon: MessageCircle,
      color: "text-purple-500",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-lg mx-auto max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <MessageCircle className="h-5 w-5 text-primary" />
            Wait! Help us improve
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            We noticed you're leaving. What could we do better to make your
            Kente design experience amazing?
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          {/* Reasons */}
          <div>
            <label className="text-sm font-medium mb-3 block">
              What could we improve?
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {reasons.map((reason) => (
                <button
                  key={reason.id}
                  onClick={() => setSelectedReason(reason.id)}
                  className={`p-3 sm:p-4 rounded-lg border-2 text-left transition-all ${
                    selectedReason === reason.id
                      ? "border-primary bg-primary/10"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <reason.icon
                    className={`h-5 w-5 sm:h-6 sm:w-6 ${reason.color} mb-2`}
                  />
                  <div className="font-medium text-sm sm:text-base">
                    {reason.label}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">
                    {reason.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Additional feedback (optional)
            </label>
            <textarea
              placeholder="Tell us more about your experience..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full p-3 border border-gray-200 rounded-lg resize-none min-h-[80px] sm:min-h-[100px] text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Fixed Action Buttons */}
        <div className="flex-shrink-0 pt-4 border-t bg-background">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1 h-10 sm:h-auto"
              disabled={isSubmitting}
            >
              <X className="h-4 w-4 mr-2" />
              Skip
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 h-10 sm:h-auto"
              disabled={!selectedReason || isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Feedback"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentPopup;
